import { Component, effect, inject, OnDestroy, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VariationEditorPage } from '@apps/mtjr/vendors/app/product/attributes/variation-editor.page';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNav,
  IonNavLink,
  IonTitle,
  IonToolbar,
  ModalController,
  NavParams,
} from '@ionic/angular/standalone';
import { VxCurrencyPipe } from '@libs/core/lib/pipes/mtjr-currency.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'mav-variants',
  imports: [
    IonHeader,
    IonTitle,
    IonButtons,
    IonContent,
    IonToolbar,
    IonList,
    IonItem,
    IonLabel,
    IonButton,
    IonIcon,
    VxCurrencyPipe,
    IonNavLink,
    TranslatePipe,
    IonCard,
  ],
  template: `
    <ion-header [translucent]="true">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button (click)="modalCtr.dismiss()">{{ 'COMMON.CLOSE' | translate }}</ion-button>
        </ion-buttons>
        <ion-title>{{ 'PRODUCTS.VARIANTS' | translate }}</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content (pointerdown)="$event.preventDefault(); $event.stopPropagation()">
      <ion-list class="mb-6 mt-4 ">
        @for (variant of attributesStore.variants(); track $index) {
          <ion-nav-link [component]="component" [componentProps]="{ attributesStore, variant }">
            <ion-card class="cursor-pointer hover:bg-medium/10">
              <ion-item detail="true" class="my-2" lines="none">
                @if (variant.image) {
                  <img slot="start" [src]="variant.image" class="size-14" />
                } @else {
                  <div class="flex size-14 rounded-xl border-2 border-dashed border-medium/50">
                    <ion-icon slot="start" class="m-auto" color="medium" icon="image-outline" />
                  </div>
                }
                <div class="ms-3 flex items-center">
                  @for (attr of variant.attributes; track $index) {
                    @if (attr.type === 'color') {
                      <span
                        [style.backgroundColor]="attr.key"
                        class="size-6 inline-block rounded-lg border-solid border-[2px] border-medium"
                      ></span>
                    } @else {
                      <span>{{ attr.label }}</span>
                    }
                    @if ($index < variant.attributes.length - 1) {
                      <span class="mx-1 text-medium ">/</span>
                    }
                  }
                </div>
                <ion-label class="!text-medium !text-sm" slot="end">{{ variant.price | vxCurrency }}</ion-label>
              </ion-item>
            </ion-card>
          </ion-nav-link>
        }
      </ion-list>
    </ion-content>
  `,
})
export class VariantsSheetComponent {
  attributesStore = inject(NavParams).get('attributesStore');
  modalCtr = inject(ModalController);
  component = VariationEditorPage;
}

@Component({
  selector: 'mav-variants-modal',
  imports: [IonNav],
  template: `
    <ion-nav />
  `,
})
export class VariantsModalComponent implements OnDestroy {
  static modalId = 'varm';
  navParams = inject(NavParams);
  attributesStore = this.navParams.get('attributesStore');
  modalCtrl = inject(ModalController);
  ionNav = viewChild(IonNav, { read: IonNav });
  route = inject(ActivatedRoute);
  router = inject(Router);
  routeSub?: Subscription;
  navSub?: Subscription;

  constructor() {
    effect(async () => {
      const nav = this.ionNav();
      if (!nav) return;
      if (!nav.root) {
        await nav.setRoot(VariantsSheetComponent, { attributesStore: this.attributesStore });
      }
      this.navSub = nav.ionNavDidChange.subscribe(async () => {
        const active = await nav.getActive();
        const tagName = active?.element?.tagName?.toLowerCase();
        if (tagName) {
          this.router.navigate([], {
            queryParams: { varm: tagName },
            queryParamsHandling: 'merge',
            relativeTo: this.route,
          });
        }
      });
    });

    this.routeSub = this.route.queryParams.subscribe(async (params) => {
      const nav = this.ionNav();
      if (!nav) return;
      const canGoBack = await nav.canGoBack();
      const varm = params[VariantsModalComponent.modalId];
      if (!varm) {
        this.modalCtrl.dismiss();
      } else {
        if (varm === 'mav-variants' && canGoBack) {
          await nav.pop();
        } else if (varm === 'mav-variation-editor' && !canGoBack) {
          await nav.push(VariationEditorPage, {
            attributesStore: this.attributesStore,
            variant: this.attributesStore.lastSelectedVariant(),
          });
        }
      }
    });
  }
  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.navSub?.unsubscribe();
    this.router.navigate([], {
      queryParams: { varm: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    });
  }
}
